import { NgFor, NgIf } from '@angular/common';
import { Component, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { dataLocalParaInput, LocalDatePipe, LocalDateTimePipe } from '../../core/date-utils';
import { Cliente, PagamentoAjusteIndividual, PagamentoPix, PagamentoRetirada } from '../../core/models';

type AbaPagamentos = 'consulta' | 'cadastro';

interface RetiradaResumo {
  id_retirada: number;
  id_pagamento?: number;
  numero_nota: string;
  cliente: string;
  data_retirada: string;
  responsaveis: string[];
  numero_pagamento?: string;
  tipo_documento?: string;
  numero_documento?: string;
  valor_unitario: number;
  valor_documento?: number | null;
  ajustes_individuais?: PagamentoAjusteIndividual[];
  data_pagamento?: string;
  status?: string | null;
  finalizado_por_nome?: string;
  finalizado_em?: string;
  observacao_pagamento?: string;
  total_quantidade: number;
  total_pagamento: number;
}

interface PagamentoContagemResumo {
  id_contagem: number;
  data_contagem: string;
  responsaveis: string[];
  responsaveis_detalhes?: { nome: string; percentual_pagamento: number }[];
  quantidade: number;
}

interface PagamentoPreviaParte {
  data_contagem: string;
  responsaveis: string[];
  responsaveis_detalhes?: { nome: string; percentual_pagamento: number }[];
  quantidade: number;
  total: number;
}

interface PagamentoPreviaLinha {
  data_contagem: string;
  id_produto: number;
  codigo_produto: string;
  produto: string;
  numero_nota: string;
  cliente: string;
  quantidade: number;
  valor_unitario: number;
  total: number;
  partes: PagamentoPreviaParte[];
  status?: string | null;
  responsaveis_detalhes?: { nome: string; percentual_pagamento: number }[];
  percentuais_responsaveis?: Record<string, number>;
}

interface PagamentoHistorico {
  id_pagamento: number;
  numero_pagamento: string;
  documento: string;
  clientes: string;
  retiradas: number[];
  data_pagamento?: string;
  status?: string | null;
  finalizado_por_nome?: string;
  finalizado_em?: string;
  valor_documento?: number | null;
  ajustes_individuais?: PagamentoAjusteIndividual[];
  total_quantidade: number;
  total_pagamento: number;
}

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, LocalDatePipe, LocalDateTimePipe],
  template: `
    <header class="page-header">
      <div><h2>Pagamentos de produção</h2><p>Consulta e lançamento dos pagamentos das retiradas finalizadas</p></div>
    </header>

    <nav class="tabs" aria-label="Abas de pagamentos">
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'consulta'" (click)="selecionarAba('consulta')">Consulta</button>
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'cadastro'" (click)="selecionarAba('cadastro')">{{ form.controls.id_pagamento.value ? 'Editar pagamento' : 'Cadastrar pagamento' }}</button>
    </nav>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <div class="modal-backdrop" *ngIf="pixPagamentoAberto">
      <section class="modal pix-modal">
        <div class="modal-header">
          <div>
            <h3>Pix {{ pixPagamentoAberto.numero_pagamento }}</h3>
            <p>QR Codes por responsavel com valor liquido do pagamento finalizado</p>
          </div>
          <button class="ghost modal-close" type="button" (click)="fecharPix()">X</button>
        </div>

        <p class="field-hint" *ngIf="carregandoPix">Gerando QR Codes Pix...</p>
        <div class="pix-workspace" *ngIf="!carregandoPix">
          <div class="pix-list">
            <button
              class="pix-list-item"
              type="button"
              *ngFor="let item of pixItens"
              [class.active]="pixSelecionado?.responsavel === item.responsavel"
              [class.pix-paid]="item.pago"
              [class.pix-pending]="item.pendencia"
              (click)="selecionarPix(item)"
            >
              <span>
                <strong>{{ item.responsavel }}</strong>
                <small *ngIf="item.pago">Pago em {{ item.pago_em | localDateTime }}</small>
                <small *ngIf="!item.pago && item.pendencia">{{ item.pendencia }}</small>
              </span>
              <b>{{ formatMoeda(item.valor) }}</b>
              <em>{{ item.pago ? 'Pago' : 'Pendente' }}</em>
            </button>
          </div>

          <article class="pix-detail" *ngIf="pixSelecionado as item; else semPixSelecionado">
            <div class="pix-detail-header">
              <div>
                <strong>{{ item.responsavel }}</strong>
                <span>{{ formatMoeda(item.valor) }}</span>
              </div>
              <em [class.pix-paid-status]="item.pago">{{ item.pago ? 'Pago' : 'Pendente' }}</em>
            </div>

            <div class="pix-qr-stage" *ngIf="item.qr_code; else pixSemQr">
              <img [src]="item.qr_code" [alt]="'QR Code Pix de ' + item.responsavel">
            </div>
            <ng-template #pixSemQr>
              <p class="error">{{ item.pendencia || 'QR Code indisponivel' }}</p>
            </ng-template>

            <dl>
              <dt>Chave Pix</dt>
              <dd>{{ item.chave_pix || '-' }}</dd>
              <dt>Pix copia e cola</dt>
              <dd class="pix-copy">{{ item.copia_cola || '-' }}</dd>
            </dl>

            <div class="form-actions">
              <button type="button" (click)="copiarPix(item)" [disabled]="!item.copia_cola">Copiar Pix</button>
              <button type="button" (click)="marcarPixComoPago(item, true)" [disabled]="item.pago || marcandoPixPago || !item.qr_code">Marcar como pago</button>
              <button class="ghost" type="button" (click)="marcarPixComoPago(item, false)" [disabled]="!item.pago || marcandoPixPago">Desmarcar pago</button>
            </div>
          </article>
          <ng-template #semPixSelecionado>
            <p class="field-hint">Selecione um responsavel na lista.</p>
          </ng-template>
        </div>
      </section>
    </div>

    <section class="panel" *ngIf="abaAtiva === 'consulta'">
      <form class="grid-form" [formGroup]="filtros" (ngSubmit)="carregar()">
        <label>Cliente
          <select formControlName="id_cliente">
            <option value="">Todos</option>
            <option *ngFor="let cliente of clientes" [value]="cliente.id_cliente">{{ cliente.nome }}</option>
          </select>
        </label>
        <label>Data inicial<input type="date" formControlName="data_inicial"></label>
        <label>Data final<input type="date" formControlName="data_final"></label>
        <label>Número documento<input formControlName="numero_documento"></label>
        <label>Situação
          <select formControlName="situacao">
            <option value="">Todas</option>
            <option value="pendente">Pendente</option>
            <option value="em_andamento">Em andamento</option>
            <option value="finalizado">Finalizado</option>
          </select>
        </label>
        <button type="submit">Pesquisar</button>
      </form>
    </section>

    <section class="panel" *ngIf="abaAtiva === 'cadastro'">
      <form class="grid-form" [formGroup]="form" (ngSubmit)="salvar()" novalidate>
        <input type="hidden" formControlName="id_pagamento">
        <label>Documento emitido
          <select formControlName="tipo_documento">
            <option>NFe</option>
            <option>Ordem de pagamento</option>
            <option>Recibo</option>
            <option>Nota de débito</option>
            <option>Outro</option>
          </select>
        </label>
        <label>Número do documento<input formControlName="numero_documento"></label>
        <label>Valor do documento<input formControlName="valor_documento" inputmode="decimal"></label>
        <label>Data do pagamento<input type="date" formControlName="data_pagamento"></label>
        <label class="full">Observação<textarea rows="2" formControlName="observacao"></textarea></label>
        <div class="full">
          <h3>Retiradas finalizadas</h3>
          <table class="data-table payment-withdrawals-table" *ngIf="retiradasCadastro.length; else semRetiradasCadastro">
            <thead><tr><th></th><th>Retirada</th><th>Data</th><th>Cliente</th><th>Responsáveis</th><th>Quant.</th><th>Status</th></tr></thead>
            <tbody>
              <tr *ngFor="let retirada of retiradasCadastro" [class.selected-row]="retiradaSelecionada(retirada.id_retirada)">
                <td data-label="Selecionar">
                  <input type="checkbox" [checked]="retiradaSelecionada(retirada.id_retirada)" (change)="alternarRetirada(retirada, $any($event.target).checked)">
                </td>
                <td data-label="Retirada">{{ retirada.id_retirada }} <span class="muted-line">{{ retirada.numero_nota }}</span></td>
                <td data-label="Data">{{ retirada.data_retirada | localDate }}</td>
                <td data-label="Cliente">{{ retirada.cliente }}</td>
                <td data-label="Responsáveis">{{ retirada.responsaveis.join(', ') || '-' }}</td>
                <td data-label="Quant.">{{ retirada.total_quantidade }}</td>
                <td data-label="Status"><span class="status" [class.finalizada]="retirada.status === 'FINALIZADO'">{{ statusPagamentoLabel(retirada.status) }}</span></td>
              </tr>
            </tbody>
          </table>
          <ng-template #semRetiradasCadastro>
            <p class="field-hint">Nenhuma retirada pendente encontrada para pagamento.</p>
          </ng-template>
        </div>
        <div class="full" *ngIf="responsaveisSelecionados.length">
          <h3>Ajustes individuais</h3>
          <div class="inline-picker" [formGroup]="ajusteForm">
            <label>Responsavel
              <select formControlName="responsavel">
                <option value="">Selecione</option>
                <option *ngFor="let responsavel of responsaveisSelecionados" [value]="responsavel">{{ responsavel }}</option>
              </select>
            </label>
            <label>Data da contagem
              <select formControlName="data_contagem">
                <option value="">Todo o pagamento</option>
                <option *ngFor="let data of datasContagemPrevia" [value]="data">{{ data | localDate }}</option>
              </select>
            </label>
            <label>Tipo
              <select formControlName="tipo">
                <option value="DESCONTO">Desconto</option>
                <option value="ACRESCIMO">Valor a mais</option>
              </select>
            </label>
            <label>Valor<input formControlName="valor" inputmode="decimal"></label>
            <label>Descricao<input formControlName="descricao"></label>
            <button type="button" (click)="adicionarAjusteIndividual()">Adicionar</button>
          </div>
          <table class="data-table adjustment-table" *ngIf="ajustesIndividuais.length">
            <thead><tr><th>Responsavel</th><th>Data da contagem</th><th>Tipo</th><th>Valor</th><th>Descricao</th><th></th></tr></thead>
            <tbody>
              <tr *ngFor="let ajuste of ajustesIndividuais; let index = index">
                <td data-label="Responsavel">{{ ajuste.responsavel }}</td>
                <td data-label="Data da contagem">{{ ajuste.data_contagem ? (ajuste.data_contagem | localDate) : 'Todo pagamento' }}</td>
                <td data-label="Tipo">{{ ajusteTipoLabel(ajuste.tipo) }}</td>
                <td data-label="Valor">{{ formatMoeda(valorAjusteIndividual(ajuste)) }}</td>
                <td data-label="Descricao">{{ ajuste.descricao }}</td>
                <td class="actions" data-label="Acoes"><button class="ghost danger-action" type="button" (click)="removerAjusteIndividual(index)">Remover</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="form-actions full">
          <button type="submit" [disabled]="salvando">{{ salvando ? 'Salvando...' : 'Salvar pagamento' }}</button>
          <button class="ghost danger-action" type="button" (click)="excluirAtual()" *ngIf="form.controls.id_pagamento.value">Excluir pagamento</button>
          <button class="ghost" type="button" (click)="novoPagamento()">Limpar</button>
        </div>
      </form>
    </section>

    <section class="panel" *ngIf="abaAtiva === 'cadastro' && linhasPreviaCadastro.length">
      <h3>Prévia do pagamento</h3>
      <div class="payment-sheet-wrap">
        <table class="payment-sheet">
          <thead>
            <tr>
              <th>Data</th>
              <th *ngFor="let responsavel of responsaveisSelecionados">{{ responsavel }} ({{ percentuaisResponsavel(responsavel) }})</th>
              <th class="payment-product">Produto</th>
              <th class="payment-value">Valor</th>
              <th class="payment-quantity">Quant.</th>
              <th class="payment-total">Total</th>
              <th>Documento</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let linha of linhasPreviaCadastro">
              <td>{{ linha.data_contagem | localDate:'short' }}</td>
              <td *ngFor="let responsavel of responsaveisSelecionados">{{ valorResponsavelCadastro(linha, responsavel) }}</td>
              <td class="payment-product">
                <strong>{{ linha.produto }}</strong>
                <span class="muted-line">{{ linha.numero_nota }} - {{ linha.cliente }}</span>
              </td>
              <td class="payment-value">{{ formatMoeda(linha.valor_unitario) }}</td>
              <td class="payment-quantity">{{ formatQuantidade(linha.quantidade) }}</td>
              <td class="payment-total">{{ formatMoeda(linha.total) }}</td>
              <td>{{ form.controls.tipo_documento.value }} {{ form.controls.numero_documento.value }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr *ngIf="ajusteDocumentoCadastro !== 0">
              <th>Diferença para o Caixa</th>
              <th *ngFor="let responsavel of responsaveisSelecionados"></th>
              <th class="payment-fill" colspan="3"></th>
              <th class="payment-total">{{ formatMoeda(ajusteDocumentoCadastro) }}</th>
              <th></th>
            </tr>
            <tr *ngFor="let ajuste of ajustesIndividuais">
              <th>{{ ajusteResumoLabel(ajuste) }}</th>
              <th *ngFor="let responsavel of responsaveisSelecionados">{{ valorAjusteResponsavelLinha(ajuste, responsavel) }}</th>
              <th class="payment-fill" colspan="3"></th>
              <th class="payment-total"></th>
              <th></th>
            </tr>
            <tr>
              <th>Total</th>
              <th *ngFor="let responsavel of responsaveisSelecionados">{{ totalResponsavelCadastro(responsavel) }}</th>
              <th class="payment-fill" colspan="3"></th>
              <th class="payment-total">{{ formatMoeda(totalLiquidoCadastro) }}</th>
              <th></th>
            </tr>
            <tr>
              <th>Caixa da empresa</th>
              <th class="payment-fill" [attr.colspan]="responsaveisSelecionados.length + 3"></th>
              <th class="payment-total">{{ formatMoeda(totalCaixaEmpresaCadastro) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>

    <section class="panel" *ngIf="abaAtiva === 'consulta'">
      <table class="data-table" *ngIf="pagamentosHistorico.length; else semHistorico">
        <thead><tr><th>Pagamento</th><th>Documento</th><th>Cliente</th><th>Retiradas</th><th>Data</th><th>Quant.</th><th>Total</th><th>Status</th><th>Finalizado por</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let pagamento of pagamentosHistorico">
            <td data-label="Pagamento"><strong>{{ pagamento.numero_pagamento }}</strong></td>
            <td data-label="Documento">{{ pagamento.documento }}</td>
            <td data-label="Cliente">{{ pagamento.clientes }}</td>
            <td data-label="Retiradas">{{ pagamento.retiradas.join(', ') }}</td>
            <td data-label="Data">{{ pagamento.data_pagamento | localDate }}</td>
            <td data-label="Quant.">{{ pagamento.total_quantidade }}</td>
            <td data-label="Total">{{ formatMoeda(pagamento.total_pagamento) }}</td>
            <td data-label="Status"><span class="status" [class.finalizada]="pagamento.status === 'FINALIZADO'">{{ statusPagamentoLabel(pagamento.status) }}</span></td>
            <td data-label="Finalizado por">
              <span>{{ pagamento.finalizado_por_nome || '-' }}</span>
              <span class="muted-line" *ngIf="pagamento.finalizado_em">{{ pagamento.finalizado_em | localDateTime }}</span>
            </td>
            <td class="actions" data-label="Ações">
              <button type="button" (click)="editarHistorico(pagamento)" *ngIf="pagamento.status !== 'FINALIZADO'">Editar</button>
              <button type="button" (click)="finalizar(pagamento)" *ngIf="pagamento.status === 'EM_ANDAMENTO'">Finalizar</button>
              <button class="ghost danger-action" type="button" (click)="excluir(pagamento)" *ngIf="pagamento.status !== 'FINALIZADO'">Excluir</button>
              <button class="ghost" type="button" (click)="reabrir(pagamento)" *ngIf="pagamento.status === 'FINALIZADO'">Reabrir</button>
              <button type="button" (click)="abrirPix(pagamento)" *ngIf="pagamento.status === 'FINALIZADO'">Pix</button>
              <button class="ghost" type="button" (click)="pdf(pagamento)">PDF</button>
            </td>
          </tr>
        </tbody>
      </table>
      <ng-template #semHistorico>
        <p class="field-hint">Nenhum pagamento encontrado no histórico.</p>
      </ng-template>
    </section>
  `
})
export class PagamentosComponent implements OnInit, DoCheck, OnDestroy {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  clientes: Cliente[] = [];
  pagamentos: PagamentoRetirada[] = [];
  erro = '';
  mensagem = '';
  salvando = false;
  abaAtiva: AbaPagamentos = 'consulta';
  retiradasSelecionadas: number[] = [];
  ajustesIndividuais: PagamentoAjusteIndividual[] = [];
  pixPagamentoAberto?: PagamentoHistorico;
  pixItens: PagamentoPix[] = [];
  pixSelecionado?: PagamentoPix;
  carregandoPix = false;
  marcandoPixPago = false;
  private avisoAtual = '';
  private avisoTimer?: ReturnType<typeof setTimeout>;

  filtros = this.fb.nonNullable.group({
    id_cliente: [''],
    data_inicial: [''],
    data_final: [''],
    numero_documento: [''],
    situacao: ['']
  });

  form = this.fb.nonNullable.group({
    id_pagamento: [0],
    tipo_documento: ['NFe', Validators.required],
    numero_documento: ['', Validators.required],
    valor_documento: ['', Validators.required],
    data_pagamento: [dataLocalParaInput(), Validators.required],
    observacao: ['']
  });

  ajusteForm = this.fb.nonNullable.group({
    responsavel: [''],
    data_contagem: [''],
    tipo: ['DESCONTO' as PagamentoAjusteIndividual['tipo']],
    valor: [''],
    descricao: ['']
  });

  ngOnInit() {
    this.api.clientes().subscribe((clientes) => (this.clientes = clientes));
    this.carregar();
  }

  ngDoCheck() {
    const aviso = this.erro || this.mensagem;
    if (aviso === this.avisoAtual) return;

    this.avisoAtual = aviso;
    this.agendarFechamentoAviso(aviso);
  }

  ngOnDestroy() {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  carregar() {
    this.api.pagamentos(this.filtros.getRawValue()).subscribe({
      next: (pagamentos) => (this.pagamentos = pagamentos),
      error: (error) => (this.erro = error.error?.message || 'Erro ao carregar pagamentos')
    });
  }

  selecionarAba(aba: AbaPagamentos) {
    this.abaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
  }

  salvar() {
    this.erro = '';
    this.mensagem = '';
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.retiradasSelecionadas.length) {
      this.erro = 'Revise os campos do pagamento antes de salvar';
      return;
    }

    const raw = this.form.getRawValue();
    this.salvando = true;
    this.api.salvarPagamento({
      id_pagamento: raw.id_pagamento || undefined,
      id_retiradas: this.retiradasSelecionadas,
      tipo_documento: raw.tipo_documento,
      numero_documento: raw.numero_documento,
      valor_documento: this.parseValor(raw.valor_documento),
      ajustes_individuais: this.ajustesIndividuaisPayload(),
      data_pagamento: raw.data_pagamento,
      observacao: raw.observacao
    }).subscribe({
      next: () => {
        this.mensagem = 'Pagamento salvo com sucesso';
        this.novoPagamento();
        this.abaAtiva = 'consulta';
        this.carregar();
      },
      error: (error) => {
        this.erro = error.error?.message || 'Erro ao salvar pagamento';
        this.salvando = false;
      },
      complete: () => (this.salvando = false)
    });
  }

  editar(linha: PagamentoRetirada) {
    const retirada = this.retiradasResumo.find((item) => item.id_retirada === linha.id_retirada);
    const retiradasMesmoDocumento = linha.id_pagamento
      ? this.retiradasResumo.filter((item) =>
        item.id_pagamento
        && item.numero_pagamento === linha.numero_pagamento
      )
      : [];
    const grupo = retiradasMesmoDocumento.length ? retiradasMesmoDocumento : retirada ? [retirada] : [];

    this.retiradasSelecionadas = grupo.map((item) => item.id_retirada);
    this.ajustesIndividuais = this.cloneAjustesIndividuais(retirada?.ajustes_individuais || linha.ajustes_individuais || []);
    this.removerAjustesSemResponsavel();
    const valorDocumento = retirada?.valor_documento
      ?? grupo.reduce((sum, item) => sum + Number(item.total_pagamento || 0), 0);
    this.form.patchValue({
      id_pagamento: retirada?.id_pagamento || 0,
      tipo_documento: retirada?.tipo_documento || 'NFe',
      numero_documento: retirada?.numero_documento || '',
      valor_documento: this.formatNumero(valorDocumento),
      data_pagamento: retirada?.data_pagamento || dataLocalParaInput(),
      observacao: retirada?.observacao_pagamento || ''
    });
    this.abaAtiva = 'cadastro';
    this.erro = '';
    this.mensagem = '';
  }

  editarHistorico(pagamento: PagamentoHistorico) {
    const linha = this.pagamentos.find((item) => item.id_pagamento === pagamento.id_pagamento);
    if (linha) this.editar(linha);
  }

  finalizar(pagamento: PagamentoHistorico) {
    const confirmar = window.confirm(`Finalizar o pagamento ${pagamento.numero_pagamento}?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.finalizarPagamento(pagamento.id_pagamento).subscribe({
      next: () => {
        this.mensagem = `Pagamento ${pagamento.numero_pagamento} finalizado com sucesso`;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao finalizar pagamento')
    });
  }

  reabrir(pagamento: PagamentoHistorico) {
    const confirmar = window.confirm(`Reabrir o pagamento ${pagamento.numero_pagamento}?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.reabrirPagamento(pagamento.id_pagamento).subscribe({
      next: () => {
        this.mensagem = `Pagamento ${pagamento.numero_pagamento} reaberto com sucesso`;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao reabrir pagamento')
    });
  }

  excluir(pagamento: PagamentoHistorico) {
    const confirmar = window.confirm(`Excluir o pagamento ${pagamento.numero_pagamento} e liberar as retiradas?`);
    if (!confirmar) return;

    this.excluirPorId(pagamento.id_pagamento, pagamento.numero_pagamento);
  }

  excluirAtual() {
    const idPagamento = Number(this.form.controls.id_pagamento.value || 0);
    if (!idPagamento) return;

    const pagamento = this.pagamentosHistorico.find((item) => item.id_pagamento === idPagamento);
    const numeroPagamento = pagamento?.numero_pagamento || `#${idPagamento}`;
    const confirmar = window.confirm(`Excluir o pagamento ${numeroPagamento} e liberar as retiradas?`);
    if (!confirmar) return;

    this.excluirPorId(idPagamento, numeroPagamento, true);
  }

  pdf(pagamento: PagamentoHistorico) {
    this.api.abrirPagamento(pagamento.id_pagamento);
  }

  abrirPix(pagamento: PagamentoHistorico) {
    this.erro = '';
    this.mensagem = '';
    this.pixPagamentoAberto = pagamento;
    this.pixItens = [];
    this.pixSelecionado = undefined;
    this.carregandoPix = true;

    this.api.pagamentoPix(pagamento.id_pagamento).subscribe({
      next: (itens) => {
        this.pixItens = itens;
        this.pixSelecionado = itens[0];
      },
      error: (error) => {
        this.erro = error.error?.message || 'Erro ao gerar QR Codes Pix';
        this.fecharPix();
      },
      complete: () => (this.carregandoPix = false)
    });
  }

  fecharPix() {
    this.pixPagamentoAberto = undefined;
    this.pixItens = [];
    this.pixSelecionado = undefined;
    this.carregandoPix = false;
    this.marcandoPixPago = false;
  }

  selecionarPix(item: PagamentoPix) {
    this.pixSelecionado = item;
  }

  copiarPix(item: PagamentoPix) {
    if (!item.copia_cola) return;
    navigator.clipboard?.writeText(item.copia_cola).then(() => {
      this.mensagem = `Pix de ${item.responsavel} copiado`;
    }).catch(() => {
      this.erro = 'Nao foi possivel copiar o Pix automaticamente';
    });
  }

  marcarPixComoPago(item: PagamentoPix, pago: boolean) {
    const pagamento = this.pixPagamentoAberto;
    if (!pagamento || this.marcandoPixPago) return;

    this.erro = '';
    this.mensagem = '';
    this.marcandoPixPago = true;
    this.api.marcarPagamentoPixPago(pagamento.id_pagamento, item.responsavel, pago).subscribe({
      next: (itens) => {
        this.pixItens = itens;
        this.pixSelecionado = itens.find((pix) => pix.responsavel === item.responsavel) || itens[0];
        this.mensagem = pago ? `Pix de ${item.responsavel} marcado como pago` : `Pix de ${item.responsavel} desmarcado`;
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao atualizar status do Pix'),
      complete: () => (this.marcandoPixPago = false)
    });
  }

  novoPagamento() {
    this.retiradasSelecionadas = [];
    this.ajustesIndividuais = [];
    this.ajusteForm.reset({
      responsavel: '',
      tipo: 'DESCONTO',
      valor: '',
      descricao: ''
    });
    this.form.reset({
      id_pagamento: 0,
      tipo_documento: 'NFe',
      numero_documento: '',
      valor_documento: '',
      data_pagamento: dataLocalParaInput(),
      observacao: ''
    });
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
    this.avisoAtual = '';
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  alternarRetirada(retirada: RetiradaResumo, selecionada: boolean) {
    this.erro = '';
    this.mensagem = '';
    if (retirada.status === 'FINALIZADO') return;
    if (!selecionada) {
      this.retiradasSelecionadas = this.retiradasSelecionadas.filter((id) => id !== retirada.id_retirada);
      this.removerAjustesSemResponsavel();
      return;
    }

    this.retiradasSelecionadas = [...new Set([...this.retiradasSelecionadas, retirada.id_retirada])];
    if (!this.form.controls.id_pagamento.value && retirada.id_pagamento) {
      this.ajustesIndividuais = this.cloneAjustesIndividuais(retirada.ajustes_individuais || []);
      this.form.patchValue({
        id_pagamento: retirada.id_pagamento,
        tipo_documento: retirada.tipo_documento || 'NFe',
        numero_documento: retirada.numero_documento || '',
        valor_documento: this.formatNumero(retirada.valor_documento ?? (retirada.total_pagamento || 0)),
        data_pagamento: retirada.data_pagamento || dataLocalParaInput(),
        observacao: retirada.observacao_pagamento || ''
      });
    }
    this.removerAjustesSemResponsavel();
  }

  retiradaSelecionada(idRetirada: number) {
    return this.retiradasSelecionadas.includes(idRetirada);
  }

  valorResponsavel(linha: PagamentoRetirada, responsavel: string) {
    if (!linha.id_pagamento || !linha.responsaveis.includes(responsavel)) return '';
    return this.formatMoeda(this.totalResponsavelLinhaConsulta(linha, responsavel));
  }

  totalResponsavel(responsavel: string) {
    const total = this.pagamentos.reduce((sum, linha) => {
      if (!linha.id_pagamento || !linha.responsaveis.includes(responsavel)) return sum;
      return sum + this.totalResponsavelLinhaConsulta(linha, responsavel);
    }, 0);
    return this.formatMoeda(total);
  }

  valorResponsavelCadastro(linha: PagamentoPreviaLinha, responsavel: string) {
    const valor = this.valorResponsavelLinhaPrevia(linha, responsavel);
    return valor ? this.formatMoeda(valor) : '';
  }

  totalResponsavelCadastro(responsavel: string) {
    return this.formatMoeda(this.totalResponsavelCadastroNumero(responsavel));
  }

  private totalResponsavelCadastroNumero(responsavel: string) {
    const total = this.linhasPreviaCadastro.reduce((sum, linha) => sum + this.valorResponsavelLinhaPrevia(linha, responsavel), 0);
    return total + this.totalAjusteIndividualResponsavel(responsavel);
  }

  get totalCaixaEmpresaCadastro() {
    const totalResponsaveis = this.responsaveisSelecionados
      .reduce((sum, responsavel) => sum + this.totalResponsavelCadastroNumero(responsavel), 0);
    return this.totalLiquidoCadastro - totalResponsaveis;
  }

  ajusteResponsavelCadastro(responsavel: string) {
    return '';
  }

  adicionarAjusteIndividual() {
    const raw = this.ajusteForm.getRawValue();
    const responsavel = String(raw.responsavel || '').trim();
    const data_contagem = String(raw.data_contagem || '').trim() || null;
    const tipo = raw.tipo === 'ACRESCIMO' ? 'ACRESCIMO' : 'DESCONTO';
    const valor = this.parseValor(raw.valor);
    const descricao = String(raw.descricao || '').trim();

    this.erro = '';
    this.mensagem = '';
    if (!this.responsaveisSelecionados.includes(responsavel)) {
      this.erro = 'Selecione um responsavel valido para o ajuste';
      return;
    }
    if (valor <= 0) {
      this.erro = 'Informe um valor maior que zero para o ajuste';
      return;
    }
    if (!descricao) {
      this.erro = 'Informe a descricao do ajuste';
      return;
    }

    this.ajustesIndividuais = [
      ...this.ajustesIndividuais,
      { responsavel, data_contagem, tipo, valor, descricao }
    ];
    this.ajusteForm.reset({
      responsavel,
      data_contagem: data_contagem || '',
      tipo: 'DESCONTO',
      valor: '',
      descricao: ''
    });
  }

  removerAjusteIndividual(index: number) {
    this.ajustesIndividuais = this.ajustesIndividuais.filter((_, itemIndex) => itemIndex !== index);
  }

  ajusteTipoLabel(tipo: PagamentoAjusteIndividual['tipo']) {
    return tipo === 'ACRESCIMO' ? 'Valor a mais' : 'Desconto';
  }

  ajusteResumoLabel(ajuste: PagamentoAjusteIndividual) {
    const data = ajuste.data_contagem ? ` (${this.formatDataContagem(ajuste.data_contagem)})` : '';
    return `${this.ajusteTipoLabel(ajuste.tipo)}: ${ajuste.descricao}${data}`;
  }

  valorAjusteIndividual(ajuste: PagamentoAjusteIndividual) {
    const valor = Math.abs(Number(ajuste.valor || 0));
    return ajuste.tipo === 'ACRESCIMO' ? valor : -valor;
  }

  valorAjusteResponsavelLinha(ajuste: PagamentoAjusteIndividual, responsavel: string) {
    return ajuste.responsavel === responsavel ? this.formatMoeda(this.valorAjusteIndividual(ajuste)) : '';
  }

  totalLinhaCadastro(linha: PagamentoRetirada) {
    return Number(linha.quantidade || 0) * Number(linha.valor_unitario || 0);
  }

  statusPagamentoLabel(status?: string | null) {
    const labels: Record<string, string> = {
      EM_ANDAMENTO: 'Em andamento',
      FINALIZADO: 'Finalizado',
      CANCELADO: 'Cancelado'
    };
    return status ? labels[status] || status : 'Pendente';
  }

  formatMoeda(value: string | number | null | undefined) {
    const parsed = this.parseValor(value);
    return parsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatQuantidade(value: string | number | null | undefined) {
    const quantidade = Number(value || 0);
    return quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  }

  get responsaveis() {
    return [...new Set(this.pagamentos.flatMap((linha) => linha.responsaveis || []))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  get responsaveisSelecionados() {
    return [...new Set(this.linhasPreviaCadastro.flatMap((linha) => linha.partes.flatMap((parte) => parte.responsaveis || [])))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  get datasContagemPrevia() {
    return [...new Set(this.linhasPreviaCadastro.flatMap((linha) => linha.partes.map((parte) => parte.data_contagem).filter(Boolean)))]
      .sort((a, b) => b.localeCompare(a));
  }

  get pagamentosHistorico() {
    const map = new Map<string, PagamentoHistorico>();

    for (const linha of this.pagamentos.filter((item) => item.id_pagamento && item.numero_pagamento)) {
      const key = String(linha.numero_pagamento);
      const atual = map.get(key) || {
        id_pagamento: Number(linha.id_pagamento),
        numero_pagamento: key,
        documento: [linha.tipo_documento, linha.numero_documento].filter(Boolean).join(' '),
        clientes: '',
        retiradas: [],
        data_pagamento: linha.data_pagamento,
        status: linha.status,
        finalizado_por_nome: linha.finalizado_por_nome,
        finalizado_em: linha.finalizado_em,
        valor_documento: linha.valor_documento,
        ajustes_individuais: linha.ajustes_individuais || [],
        total_quantidade: 0,
        total_pagamento: 0
      };
      atual.total_quantidade += Number(linha.quantidade || 0);
      atual.total_pagamento += Number(linha.total || 0);
      atual.valor_documento = atual.valor_documento ?? linha.valor_documento;
      if (!atual.ajustes_individuais?.length && linha.ajustes_individuais?.length) {
        atual.ajustes_individuais = linha.ajustes_individuais;
      }
      atual.retiradas = [...new Set([...atual.retiradas, linha.id_retirada])].sort((a, b) => a - b);
      atual.clientes = [...new Set([...(atual.clientes ? atual.clientes.split(', ') : []), linha.cliente].filter(Boolean))].join(', ');
      map.set(key, atual);
    }

    return [...map.values()].map((pagamento) => ({
      ...pagamento,
      total_pagamento: pagamento.valor_documento ?? pagamento.total_pagamento
    }));
  }

  get retiradasResumo() {
    const map = new Map<number, RetiradaResumo>();

    for (const linha of this.pagamentos) {
      const atual = map.get(linha.id_retirada) || {
        id_retirada: linha.id_retirada,
        id_pagamento: linha.id_pagamento,
        numero_pagamento: linha.numero_pagamento,
        numero_nota: linha.numero_nota,
        cliente: linha.cliente,
        data_retirada: linha.data_retirada,
        responsaveis: linha.responsaveis,
        tipo_documento: linha.tipo_documento,
        numero_documento: linha.numero_documento,
        valor_unitario: linha.valor_unitario,
        valor_documento: linha.valor_documento,
        ajustes_individuais: linha.ajustes_individuais || [],
        data_pagamento: linha.data_pagamento,
        status: linha.status,
        finalizado_por_nome: linha.finalizado_por_nome,
        finalizado_em: linha.finalizado_em,
        observacao_pagamento: linha.observacao_pagamento,
        total_quantidade: 0,
        total_pagamento: 0
      };
      atual.id_pagamento = atual.id_pagamento || linha.id_pagamento;
      if (!atual.ajustes_individuais?.length && linha.ajustes_individuais?.length) {
        atual.ajustes_individuais = linha.ajustes_individuais;
      }
      atual.total_quantidade += linha.quantidade;
      atual.total_pagamento += linha.total;
      map.set(linha.id_retirada, atual);
    }

    return [...map.values()];
  }

  get retiradasCadastro() {
    return this.retiradasResumo.filter((retirada) => retirada.status !== 'FINALIZADO');
  }

  get linhasRetiradaSelecionada() {
    return this.pagamentos.filter((linha) => this.retiradasSelecionadas.includes(linha.id_retirada));
  }

  get linhasPreviaCadastro() {
    const map = new Map<string, PagamentoPreviaLinha>();

    for (const linha of this.linhasRetiradaSelecionada) {
      for (const parte of this.partesLinhaCadastro(linha)) {
        const key = [
          parte.data_contagem,
          linha.id_produto,
          linha.codigo_produto,
          linha.produto,
          linha.numero_nota,
          linha.cliente
        ].join('|');
        const atual = map.get(key) || {
          data_contagem: parte.data_contagem,
          id_produto: linha.id_produto,
          codigo_produto: linha.codigo_produto,
          produto: linha.produto,
          numero_nota: linha.numero_nota,
          cliente: linha.cliente,
          quantidade: 0,
          valor_unitario: Number(linha.valor_unitario || 0),
          total: 0,
          partes: [],
          responsaveis_detalhes: linha.responsaveis_detalhes,
          percentuais_responsaveis: linha.percentuais_responsaveis
        };

        atual.quantidade += parte.quantidade;
        atual.total += parte.total;
        atual.partes.push(parte);
        map.set(key, atual);
      }
    }

    return [...map.values()].sort((a, b) => {
      const data = String(b.data_contagem || '').localeCompare(String(a.data_contagem || ''));
      if (data) return data;
      const produto = String(a.codigo_produto || '').localeCompare(String(b.codigo_produto || ''));
      return produto || String(a.produto || '').localeCompare(String(b.produto || ''));
    });
  }

  get valorDocumentoForm() {
    return this.parseValor(this.form.controls.valor_documento.value);
  }

  get totalGeral() {
    return this.pagamentos.reduce((sum, linha) => sum + (linha.id_pagamento ? linha.total : 0), 0);
  }

  get totalGeralCadastro() {
    return this.linhasPreviaCadastro.reduce((sum, linha) => sum + Number(linha.total || 0), 0);
  }

  get totalDocumentoCadastro() {
    return this.valorDocumentoInformado ? this.valorDocumentoForm : this.totalGeralCadastro;
  }

  get totalLiquidoCadastro() {
    return this.totalDocumentoCadastro;
  }

  get totalAjustesIndividuaisCadastro() {
    return this.ajustesIndividuais.reduce((sum, ajuste) => sum + this.valorAjusteIndividual(ajuste), 0);
  }

  get ajusteDocumentoCadastro() {
    return this.valorDocumentoInformado ? this.totalDocumentoCadastro - this.totalGeralCadastro : 0;
  }

  get valorDocumentoInformado() {
    return String(this.form.controls.valor_documento.value || '').trim() !== '';
  }

  private ajustePorResponsavelCadastro(responsavel: string) {
    return 0;
  }

  private totalBaseResponsavelCadastro(responsavel: string) {
    return this.linhasPreviaCadastro.reduce((sum, linha) => sum + this.valorResponsavelLinhaPreviaBruto(linha, responsavel), 0);
  }

  private valorResponsavelLinhaPreviaBruto(linha: PagamentoPreviaLinha, responsavel: string) {
    return linha.partes.reduce((sum, parte) => {
      if (!parte.responsaveis.includes(responsavel)) return sum;
      return sum + Number(parte.total || 0) / Math.max(parte.responsaveis.length, 1);
    }, 0);
  }

  private valorResponsavelLinhaPrevia(linha: PagamentoPreviaLinha, responsavel: string) {
    return this.valoresResponsaveisLinhaPrevia(linha)[responsavel] || 0;
  }

  private valoresResponsaveisLinhaPrevia(linha: PagamentoPreviaLinha) {
    const responsaveis = [...new Set(linha.partes.flatMap((parte) => parte.responsaveis || []))].filter(Boolean);
    const valoresBrutos = Object.fromEntries(responsaveis.map((responsavel) => [
      responsavel,
      linha.partes.reduce((sum, parte) => {
        if (!parte.responsaveis.includes(responsavel)) return sum;
        const parteBruta = Number(parte.total || 0) / Math.max(parte.responsaveis.length, 1);
        return sum + parteBruta * this.percentualResponsavelParte(linha, responsavel, parte) / 100;
      }, 0)
    ]));
    const totalBruto = Object.values(valoresBrutos).reduce((sum, valor) => sum + valor, 0);
    const centavosAlocados = Math.min(
      Math.round(Math.max(Number(linha.total || 0), 0) * 100),
      Math.max(0, Math.round(totalBruto * 100))
    );
    const centavos = Object.fromEntries(responsaveis.map((responsavel) => [
      responsavel,
      Math.floor(Math.max(valoresBrutos[responsavel], 0) * 100)
    ]));
    let restantes = Math.max(0, centavosAlocados - Object.values(centavos).reduce((sum, valor) => sum + valor, 0));
    const ordem = [...responsaveis].sort((a, b) => {
      const restoA = (Math.max(valoresBrutos[a], 0) * 100) % 1;
      const restoB = (Math.max(valoresBrutos[b], 0) * 100) % 1;
      return restoB - restoA || a.localeCompare(b);
    });
    for (const responsavel of ordem) {
      if (!restantes) break;
      centavos[responsavel] += 1;
      restantes -= 1;
    }
    return Object.fromEntries(responsaveis.map((responsavel) => [responsavel, centavos[responsavel] / 100]));
  }

  percentuaisResponsavel(responsavel: string) {
    const porData = new Map<string, number>();
    for (const linha of this.linhasPreviaCadastro) {
      for (const parte of linha.partes) {
        if (!parte.responsaveis.includes(responsavel)) continue;
        porData.set(parte.data_contagem, this.percentualResponsavelParte(linha, responsavel, parte));
      }
    }
    return [...porData.entries()]
      .sort(([dataA], [dataB]) => dataB.localeCompare(dataA))
      .map(([data, percentual]) => `${percentual}% (${this.formatDataContagem(data)})`)
      .join(', ') || '100%';
  }

  private percentualResponsavelParte(
    linha: Pick<PagamentoRetirada, 'percentuais_responsaveis' | 'status' | 'responsaveis_detalhes'> | PagamentoPreviaLinha,
    responsavel: string,
    parte: PagamentoPreviaParte
  ) {
    const chaveData = `${String(parte.data_contagem || '').slice(0, 10)}|${responsavel}`;
    const percentual = linha.percentuais_responsaveis?.[chaveData]
      ?? (linha.status === 'FINALIZADO' ? linha.percentuais_responsaveis?.[responsavel] : undefined)
      ?? parte.responsaveis_detalhes?.find((detalhe) => detalhe.nome === responsavel)?.percentual_pagamento
      ?? linha.responsaveis_detalhes?.find((detalhe) => detalhe.nome === responsavel)?.percentual_pagamento
      ?? 100;
    const parsed = Number(percentual);
    return Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : 100;
  }

  private totalResponsavelLinhaConsulta(linha: PagamentoRetirada, responsavel: string) {
    return this.partesLinhaCadastro(linha).reduce((sum, parte) => {
      if (!parte.responsaveis.includes(responsavel)) return sum;
      const bruto = Number(parte.total || 0) / Math.max(parte.responsaveis.length, 1);
      return sum + bruto * this.percentualResponsavelParte(linha, responsavel, parte) / 100;
    }, 0);
  }

  private partesLinhaCadastro(linha: PagamentoRetirada): PagamentoPreviaParte[] {
    const quantidadeLinha = Number(linha.quantidade || 0);
    const totalLinha = this.totalLinhaCadastro(linha);
    const contagens = this.normalizarContagensLinha(linha);
    const totalContagens = contagens.reduce((sum, contagem) => sum + Number(contagem.quantidade || 0), 0);
    const origem = totalContagens > 0
      ? contagens
      : [{
        data_contagem: linha.data_retirada,
        responsaveis: linha.responsaveis || [],
        responsaveis_detalhes: linha.responsaveis_detalhes || [],
        quantidade: quantidadeLinha
      }];
    const totalReferencia = totalContagens > 0 ? totalContagens : quantidadeLinha;

    return origem.map((contagem) => {
      const peso = totalReferencia > 0 ? Number(contagem.quantidade || 0) / totalReferencia : 0;
      return {
        data_contagem: contagem.data_contagem || linha.data_retirada,
        responsaveis: this.normalizarResponsaveis(contagem.responsaveis?.length ? contagem.responsaveis : linha.responsaveis),
        responsaveis_detalhes: contagem.responsaveis_detalhes || [],
        quantidade: quantidadeLinha > 0 ? quantidadeLinha * peso : Number(contagem.quantidade || 0),
        total: totalLinha * peso
      };
    }).filter((parte) => parte.quantidade > 0 || parte.total > 0);
  }

  private normalizarContagensLinha(linha: PagamentoRetirada): PagamentoContagemResumo[] {
    return (Array.isArray(linha.contagens) ? linha.contagens : [])
      .map((contagem) => ({
        id_contagem: Number(contagem.id_contagem || 0),
        data_contagem: contagem.data_contagem || linha.data_retirada,
        responsaveis: this.normalizarResponsaveis(contagem.responsaveis),
        responsaveis_detalhes: contagem.responsaveis_detalhes || [],
        quantidade: Number(contagem.quantidade || 0)
      }))
      .filter((contagem) => contagem.id_contagem && contagem.quantidade > 0);
  }

  private normalizarResponsaveis(responsaveis: string[] | string | null | undefined) {
    return (Array.isArray(responsaveis) ? responsaveis : String(responsaveis || '').split(','))
      .map((responsavel) => String(responsavel || '').trim())
      .filter(Boolean);
  }

  private totalAjusteIndividualResponsavel(responsavel: string) {
    return this.ajustesIndividuais.reduce((sum, ajuste) => {
      if (ajuste.responsavel !== responsavel) return sum;
      return sum + this.valorAjusteIndividual(ajuste);
    }, 0);
  }

  private totalAjustesIndividuais(ajustes: PagamentoAjusteIndividual[]) {
    return ajustes.reduce((sum, ajuste) => sum + this.valorAjusteIndividual(ajuste), 0);
  }

  private ajustesIndividuaisPayload() {
    return this.ajustesIndividuais.map((ajuste) => ({
      responsavel: ajuste.responsavel,
      data_contagem: ajuste.data_contagem || null,
      tipo: ajuste.tipo,
      valor: Math.abs(Number(ajuste.valor || 0)),
      descricao: ajuste.descricao
    }));
  }

  private cloneAjustesIndividuais(ajustes: PagamentoAjusteIndividual[] = []) {
    return ajustes.map((ajuste) => ({
      id_ajuste: ajuste.id_ajuste,
      responsavel: String(ajuste.responsavel || '').trim(),
      data_contagem: ajuste.data_contagem ? String(ajuste.data_contagem).slice(0, 10) : null,
      tipo: ajuste.tipo === 'ACRESCIMO' ? 'ACRESCIMO' as const : 'DESCONTO' as const,
      valor: Math.abs(Number(ajuste.valor || 0)),
      descricao: String(ajuste.descricao || '').trim()
    })).filter((ajuste) => ajuste.responsavel && ajuste.valor > 0 && ajuste.descricao);
  }

  private removerAjustesSemResponsavel() {
    const responsaveis = new Set(this.responsaveisSelecionados);
    this.ajustesIndividuais = this.ajustesIndividuais.filter((ajuste) => responsaveis.has(ajuste.responsavel));
    const responsavelAtual = this.ajusteForm.controls.responsavel.value;
    if (responsavelAtual && !responsaveis.has(responsavelAtual)) {
      this.ajusteForm.patchValue({ responsavel: '' });
    }
  }

  private formatDataContagem(value: string) {
    const [year, month, day] = value.slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
  }

  private parseValor(value: string | number | null | undefined) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const text = String(value || '0').replace(/\s/g, '').replace(/^R\$/i, '');
    const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
    const parsed = Number(normalized || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatNumero(value: number) {
    return Number(value || 0).toFixed(2).replace('.', ',');
  }

  private excluirPorId(idPagamento: number, numeroPagamento: string, limparFormulario = false) {
    this.erro = '';
    this.mensagem = '';
    this.api.excluirPagamento(idPagamento).subscribe({
      next: () => {
        this.mensagem = `Pagamento ${numeroPagamento} excluido com sucesso`;
        if (limparFormulario) {
          this.novoPagamento();
          this.abaAtiva = 'consulta';
        }
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao excluir pagamento')
    });
  }

  private agendarFechamentoAviso(aviso: string) {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
    if (!aviso) return;

    this.avisoTimer = setTimeout(() => this.fecharAviso(), 20000);
  }
}
